import { useState, useMemo } from 'react';
import HourglassDisabledIcon from '@mui/icons-material/HourglassDisabled';
import { useQuery, useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';

import {
  VaultExportFileModel,
  getVaultExportFiles,
  downloadVaultExportToDrive,
  VaultKeys,
} from '../../../api/vault-api';
import SharedFilePicker from '../../../shared/shared-file-picker';
import SharedButton from '../../../shared/buttons/shared-button';
import { ExtendedVaultExportModel } from './vault-export-grid';
import SimpleDrawer, {
  SimpleDrawerAction,
} from '../../../shared/simple-drawer-new';
import SharedReviewLayoutMainCard, {
  MainSectionItem,
} from '../../../shared/shared-review-layout-main-card';
import SharedReviewLayoutNew, {
  ReviewSection,
} from '../../../shared/shared-review-layout-new';
import { ReviewSectionItem } from '../../../shared/shared-review-layout-item';
import SharedCard from '../../../shared/shared-card';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { GridColDef } from '@mui/x-data-grid-pro';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import { useStore } from '../../state/zustand-store';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import DeleteVaultExportsDialog from './delete-vault-exports-dialog';
import {
  isAbleToSeeUIElement,
  VaultRoles,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import {
  formatBytes,
  getDeletePermissionForCorpus,
} from '../../../utils/vault-utils';
import { StatusType } from '../../../shared/status-label';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Download from '@mui/icons-material/Download';
import AttachEmail from '@mui/icons-material/AttachEmail';
import Check from '@mui/icons-material/Check';
import Delete from '@mui/icons-material/Delete';

interface VaultExportDrawerDetailsProps {
  open?: boolean;
  vaultExport: ExtendedVaultExportModel;
  onClose: () => void;
  matterName?: string;
}

function FileGridToolbar() {
  return <SharedToolbar noColumn noFilter />;
}

const formatEnumString = (val?: string) => {
  if (!val) return '';
  return val
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export default function VaultExportDrawerDetails({
  open = true,
  vaultExport,
  onClose,
  matterName,
}: Readonly<VaultExportDrawerDetailsProps>) {
  const { setSnackbar } = useStore();
  const { changeLogRoles, toolsRoles } = useUserSettings();
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [destinationFolder, setDestinationFolder] = useState<string>('');
  const [destinationFolderId, setDestinationFolderId] = useState<string>('');
  const [pendingOpenPicker, setPendingOpenPicker] = useState<
    (() => void) | null
  >(null);
  const [openDeleteVaultExports, setOpenDeleteVaultExports] = useState(false);

  const hasGlobalDelete = isAbleToSeeUIElement(
    toolsRoles,
    VaultRoles.DeleteVaultExports.value
  );
  const requiredPermission = getDeletePermissionForCorpus(vaultExport.corpus);
  const hasSpecificDelete = requiredPermission
    ? isAbleToSeeUIElement(toolsRoles, requiredPermission.value)
    : false;

  const canDelete = hasGlobalDelete || hasSpecificDelete;

  const deleteDisabledReason =
    canDelete || !requiredPermission
      ? undefined
      : Messages.Errors.Permission.Acting(requiredPermission.name);

  const actionsMenu: SimpleDrawerAction[] = useMemo(
    () => [
      {
        label: 'Delete',
        icon: <Delete fontSize="small" />,
        onClick: () => {
          setOpenDeleteVaultExports(true);
        },
        disabled: !canDelete,
        disabledReason: deleteDisabledReason,
      },
    ],
    [canDelete, deleteDisabledReason]
  );

  const daysUntilDeletionText = useMemo(() => {
    const createDate = dayjs(vaultExport.createTime);
    const expiryDate = createDate.add(15, 'day');
    const diffDays = expiryDate.diff(dayjs(), 'day');
    return diffDays <= 0
      ? 'Expired'
      : `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
  }, [vaultExport.createTime]);

  const basicInformationItems = useMemo<ReviewSectionItem[]>(() => {
    const items: ReviewSectionItem[] = [];

    if (matterName) {
      items.push({ label: 'Matter Name:', value: matterName });
    }
    if (vaultExport.corpus) {
      items.push({
        label: 'Corpus:',
        value: formatEnumString(vaultExport.corpus),
      });
    }
    if (vaultExport.dataScope) {
      items.push({
        label: 'Data Scope:',
        value: formatEnumString(vaultExport.dataScope),
      });
    }
    if (vaultExport.searchMethod) {
      items.push({
        label: 'Search Method:',
        value: formatEnumString(vaultExport.searchMethod),
      });
    }
    if (vaultExport.searchMethodTarget) {
      const label =
        vaultExport.searchMethod === 'ACCOUNT'
          ? 'Account Email:'
          : 'Search Target:';
      items.push({ label, value: vaultExport.searchMethodTarget });
    }
    if (vaultExport.timeZone) {
      items.push({ label: 'Time Zone:', value: vaultExport.timeZone });
    }
    if (vaultExport.exportFormat) {
      items.push({ label: 'Export Format:', value: vaultExport.exportFormat });
    }
    if (
      vaultExport.totalArtifactCount !== undefined &&
      vaultExport.totalArtifactCount !== null
    ) {
      items.push({
        label: 'Count:',
        value: vaultExport.totalArtifactCount.toLocaleString(),
      });
    }
    if (
      vaultExport.dataSizeBytes !== undefined &&
      vaultExport.dataSizeBytes !== null
    ) {
      items.push({
        label: 'Data Size:',
        value: formatBytes(vaultExport.dataSizeBytes),
      });
    }
    if (vaultExport.terms) {
      items.push({ label: 'Search Terms:', value: vaultExport.terms });
    }
    if (vaultExport.startTime) {
      items.push({
        label: 'Start Time:',
        value: dayjs(vaultExport.startTime).format('M/D/YY'),
      });
    }
    if (vaultExport.endTime) {
      items.push({
        label: 'End Time:',
        value: dayjs(vaultExport.endTime).format('M/D/YY'),
      });
    }

    return items;
  }, [vaultExport, matterName]);

  const mainSection: MainSectionItem = useMemo(
    () => ({
      mainTitle: vaultExport.name || 'N/A',
      status:
        vaultExport.status === 'IN_PROGRESS'
          ? 'INPROGRESS'
          : (vaultExport.status as StatusType),
      fields: [
        {
          label: 'Created Date',
          value: dayjs(vaultExport.createTime).format('M/D/YY, h:mm A'),
          icon: Check,
        },
        {
          label: 'Days Until Deletion',
          value: daysUntilDeletionText,
          icon: HourglassDisabledIcon,
        },
      ],
    }),
    [vaultExport, daysUntilDeletionText]
  );

  const leftSections: ReviewSection[] = useMemo(
    () => [
      {
        title: 'Basic Information',
        leftItems: basicInformationItems,
      },
    ],
    [basicInformationItems]
  );

  const isCompleted = vaultExport.status === 'COMPLETED';

  const { data: files, isLoading: isLoadingFiles } = useQuery({
    queryKey: [
      VaultKeys.VaultExportFiles,
      vaultExport.matterId,
      vaultExport.id,
    ],
    queryFn: () => getVaultExportFiles(vaultExport.matterId, vaultExport.id),
    enabled: !!vaultExport?.id && isCompleted,
  });

  const downloadMutation = useMutation({
    mutationFn: (vars?: { folder?: string; folderId?: string }) => {
      const fName = vars?.folder ?? destinationFolder;
      const fId = vars?.folderId ?? destinationFolderId;
      return downloadVaultExportToDrive(
        vaultExport.matterId,
        vaultExport.id,
        {
          filesToDownload: selectedFiles,
        },
        fName,
        fId,
        changeLogMessage
      );
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Vault export to drive is now running.',
        severity: 'info',
      });
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
      onClose();
    },
    onError: (error) => {
      console.error('Download failed:', error);
      setSnackbar({
        open: true,
        message: 'There was an issue starting your download.',
        severity: 'error',
      });
    },
  });

  const columns: GridColDef<VaultExportFileModel>[] = useMemo(
    () => [
      {
        field: 'fileName',
        headerName: 'File Name',
        flex: 1,
      },
      {
        field: 'size',
        headerName: 'Size',
        width: 150,
        valueFormatter: (params: number) => formatBytes(params),
      },
    ],
    []
  );

  const getSpecificDownloadRole = (corpus?: string) => {
    switch (corpus) {
      case 'MAIL':
        return VaultRoles.DownloadGmailVaultExport.value;
      case 'HANGOUTS_CHAT':
        return VaultRoles.DownloadChatVaultExport.value;
      case 'GEMINI':
        return VaultRoles.DownloadGeminiVaultExport.value;
      case 'CALENDAR':
        return VaultRoles.DownloadCalendarVaultExport.value;
      case 'DRIVE':
        return VaultRoles.DownloadDriveVaultExport.value;
      case 'GROUPS':
        return VaultRoles.DownloadGroupsVaultExport.value;
      default:
        return undefined;
    }
  };

  const getSpecificDownloadRoleName = (corpus?: string) => {
    switch (corpus) {
      case 'MAIL':
        return VaultRoles.DownloadGmailVaultExport.name;
      case 'HANGOUTS_CHAT':
        return VaultRoles.DownloadChatVaultExport.name;
      case 'GEMINI':
        return VaultRoles.DownloadGeminiVaultExport.name;
      case 'CALENDAR':
        return VaultRoles.DownloadCalendarVaultExport.name;
      case 'DRIVE':
        return VaultRoles.DownloadDriveVaultExport.name;
      case 'GROUPS':
        return VaultRoles.DownloadGroupsVaultExport.name;
      default:
        return undefined;
    }
  };

  const specificDownloadRole = getSpecificDownloadRole(vaultExport.corpus);
  const specificDownloadRoleName = getSpecificDownloadRoleName(
    vaultExport.corpus
  );

  const canDownload =
    isAbleToSeeUIElement(toolsRoles, VaultRoles.DownloadVaultExport.value) ||
    (!!specificDownloadRole &&
      isAbleToSeeUIElement(toolsRoles, specificDownloadRole));

  const hasChangeLogEnabled =
    isAbleToSeeUIElement(
      changeLogRoles,
      VaultRoles.DownloadVaultExport.value
    ) ||
    (!!specificDownloadRole &&
      isAbleToSeeUIElement(changeLogRoles, specificDownloadRole));

  const tooltipTitle = useMemo(() => {
    if (!canDownload) {
      if (specificDownloadRoleName) {
        return Messages.Errors.Permission.ActingMultiple([
          VaultRoles.DownloadVaultExport.name,
          specificDownloadRoleName,
        ]);
      }
      return Messages.Errors.Permission.Acting(
        VaultRoles.DownloadVaultExport.name
      );
    }
    if (selectedFiles.length === 0) {
      return 'You must select at least one file to download.';
    }
    return '';
  }, [canDownload, specificDownloadRoleName, selectedFiles.length]);

  function handleFolderSelected(folderId: string, folderName: string) {
    setDestinationFolder(folderName);
    setDestinationFolderId(folderId);
    downloadMutation.mutate({ folder: folderName, folderId });
  }

  function handleDownload(openDrivePicker: () => void) {
    if (!canDownload) return;
    if (hasChangeLogEnabled) {
      setPendingOpenPicker(() => openDrivePicker);
      setOpenChangeLogDialog(true);
    } else {
      openDrivePicker();
    }
  }

  return (
    <>
      <SimpleDrawer
        title="Export Details"
        open={open}
        onClose={onClose}
        actionsMenu={actionsMenu}
      >
        <Stack spacing={1.5} sx={{ width: '100%' }}>
          <SharedReviewLayoutMainCard section={mainSection} isDrawer={true} />

          <SharedReviewLayoutNew leftSections={leftSections} isDrawer={true} />

          {/* Files Section */}
          <SharedCard sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Downloadable Files
            </Typography>
            {isCompleted ? (
              <Box
                sx={{
                  height: files && files.length > 0 ? 'auto' : 350,
                  minHeight: 350,
                }}
              >
                <SharedDataGridClient
                  columns={columns}
                  rows={files || []}
                  loading={isLoadingFiles}
                  getRowId={(row) => row.objectName}
                  checkboxSelection
                  selectedRows={selectedFiles}
                  setSelectedRows={setSelectedFiles}
                  slots={{
                    toolbar: FileGridToolbar,
                    ...createGridOverlays('Files', <AttachEmail />),
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Files are not available. This export has not completed or has
                failed.
              </Typography>
            )}

            {/* Download Action Area */}
            {isCompleted && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Tooltip title={tooltipTitle} arrow>
                  <span>
                    <SharedFilePicker
                      pickerType="folder"
                      selectedFileId={destinationFolderId}
                      selectedFileName={destinationFolder}
                      onFileSelected={(doc) =>
                        handleFolderSelected(doc.id, doc.name)
                      }
                      onFileRemoved={() => {
                        setDestinationFolder('');
                        setDestinationFolderId('');
                      }}
                      includeSharedDrives
                      customTrigger={(openDrivePicker) => (
                        <SharedButton
                          variant="primary"
                          color="primary"
                          startIcon={<Download />}
                          onClick={() => handleDownload(openDrivePicker)}
                          disabled={
                            !canDownload ||
                            selectedFiles.length === 0 ||
                            downloadMutation.isPending
                          }
                        >
                          Download Selected
                        </SharedButton>
                      )}
                    />
                  </span>
                </Tooltip>
              </Box>
            )}
          </SharedCard>
        </Stack>
      </SimpleDrawer>

      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => {
          setOpenChangeLogDialog(false);
          setPendingOpenPicker(null);
        }}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          setOpenChangeLogDialog(false);
          pendingOpenPicker?.();
          setPendingOpenPicker(null);
        }}
        mutateLoading={downloadMutation.isPending}
      />

      {openDeleteVaultExports && (
        <DeleteVaultExportsDialog
          open={openDeleteVaultExports}
          handleClose={() => setOpenDeleteVaultExports(false)}
          selectedExportIds={[vaultExport.id]}
          allExports={[vaultExport]}
          matterId={vaultExport.matterId}
          setSelectedExports={(ids) => {
            if (ids.length === 0) {
              onClose();
            }
          }}
        />
      )}
    </>
  );
}
